import React, { useEffect, useMemo, useRef, useState } from 'react';
import { filterClubStands } from '../utils/clubStandList';
import './TagOutPicker.css';

/**
 * Mobile bottom sheet: browse stands by list (with search), preview on map while scrolling,
 * then tag out to the selected open stand.
 */
export function TagOutPicker({
  show,
  stands = [],
  focusedStandId,
  selectedStandId,
  onClose,
  onFocusStand,
  onSelectStand,
  onTagOut,
  onViewTagged,
  onSheetHeight
}) {
  const [search, setSearch] = useState('');
  const listRef = useRef(null);
  const sheetRef = useRef(null);
  const rowRefs = useRef(new Map());
  const focusFromScrollRef = useRef(false);

  const filteredStands = useMemo(
    () => filterClubStands(stands, search),
    [stands, search]
  );

  const selectedStand = filteredStands.find(
    (stand) => Number(stand.id) === Number(selectedStandId)
  );
  const canTagOut = Boolean(selectedStand && !selectedStand.isOccupied);

  useEffect(() => {
    if (!show) {
      setSearch('');
    }
  }, [show]);

  useEffect(() => {
    if (!show || !listRef.current) return undefined;

    const root = listRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!focusFromScrollRef.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const top = visible[0];
        if (!top) return;
        const standId = Number(top.target.getAttribute('data-stand-id'));
        if (!Number.isNaN(standId) && typeof onFocusStand === 'function') {
          onFocusStand(standId);
        }
      },
      {
        root,
        rootMargin: '-42% 0px -42% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    );

    filteredStands.forEach((stand) => {
      const row = rowRefs.current.get(stand.id);
      if (row) observer.observe(row);
    });

    return () => observer.disconnect();
  }, [show, filteredStands, onFocusStand]);

  useEffect(() => {
    if (!show || !selectedStandId || !listRef.current) return undefined;
    const row = rowRefs.current.get(selectedStandId);
    if (!row) return undefined;
    focusFromScrollRef.current = false;
    if (typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    const timer = window.setTimeout(() => {
      focusFromScrollRef.current = true;
    }, 350);
    return () => window.clearTimeout(timer);
  }, [show, selectedStandId, filteredStands.length]);

  useEffect(() => {
    if (!show) return undefined;
    focusFromScrollRef.current = true;
    return undefined;
  }, [show]);

  useEffect(() => {
    if (!show || !sheetRef.current || typeof onSheetHeight !== 'function') {
      if (!show && typeof onSheetHeight === 'function') {
        onSheetHeight(0);
      }
      return undefined;
    }

    const el = sheetRef.current;
    const report = () => onSheetHeight(el.offsetHeight || 0);
    report();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(report)
      : null;
    if (observer) observer.observe(el);
    window.addEventListener('resize', report);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', report);
    };
  }, [show, onSheetHeight, filteredStands.length, search]);

  if (!show) return null;

  return (
    <>
      <div
        className="tag-out-picker-backdrop"
        role="presentation"
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        className="tag-out-picker-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tag-out-picker-title"
      >
        <div className="tag-out-picker-handle" aria-hidden="true" />
        <header className="tag-out-picker-header">
          <h2 id="tag-out-picker-title">Tag out to a stand</h2>
          <button type="button" className="tag-out-picker-close y-btn y-btn-ghost y-btn-icon" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="tag-out-picker-search-wrap">
          <input
            type="search"
            className="tag-out-picker-search"
            placeholder="Search stands…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search stands"
          />
          <p className="tag-out-picker-hint">Scroll the list to preview stands on the map.</p>
        </div>
        <div className="tag-out-picker-list" ref={listRef}>
          {filteredStands.length === 0 ? (
            <p className="tag-out-picker-empty">No stands match your search.</p>
          ) : (
            filteredStands.map((stand) => {
              const isFocused = Number(focusedStandId) === Number(stand.id);
              const isSelected = Number(selectedStandId) === Number(stand.id);
              return (
                <button
                  key={stand.id}
                  type="button"
                  ref={(node) => {
                    if (node) rowRefs.current.set(stand.id, node);
                    else rowRefs.current.delete(stand.id);
                  }}
                  data-stand-id={stand.id}
                  className={[
                    'tag-out-picker-row',
                    isFocused ? 'is-focused' : '',
                    isSelected ? 'is-selected' : '',
                    stand.isOccupied ? 'is-occupied' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => {
                    focusFromScrollRef.current = false;
                    if (typeof onSelectStand === 'function') {
                      onSelectStand(stand.id);
                    }
                    if (typeof onFocusStand === 'function') {
                      onFocusStand(stand.id);
                    }
                    if (stand.isOccupied && typeof onViewTagged === 'function') {
                      onViewTagged(stand);
                    }
                  }}
                >
                  <span className="tag-out-picker-row-name">{stand.name}</span>
                  <span className="tag-out-picker-row-status">
                    {stand.isOccupied
                      ? (stand.occupiedBy ? `Tagged · ${stand.occupiedBy}` : 'Tagged')
                      : 'Open'}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <footer className="tag-out-picker-footer">
          <button
            type="button"
            className="tag-out-picker-action y-btn y-btn-primary y-btn-block"
            disabled={!canTagOut}
            onClick={onTagOut}
          >
            Tag out
          </button>
        </footer>
      </div>
    </>
  );
}
